import { useSelector } from 'react-redux';
import GlobalTable from '../../../components/CustomTableWithPagination/GlobalTable';
import GlobalWrapper from '../../../components/Wrapper/GlobalWrapper';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getCrmsUnderDept,
  getFeeForecastData,
  getForecastConfig,
  getManualForecast,
  getSummary,
  approveForecastOverride,
  recalculateForecastFee,
} from '../../../redux/features/feeForecastSlice';
import { Alert, Box, Button, Chip, Divider, MenuItem, Paper, Stack, TextField, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddExpectedSessions from './AddExpectedSessions';
import Config from './Config';
import ManualTriggers from './ManualTriggers';
import { FORECAST_REGISTER_TBL_HEAD } from '../../../components/datagrid/myworkspace/finance/forecast/ForecastRegisterTblHead';
import AddForecastLogs from './AddForecastLogs';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { AddButton } from '../../../components/mui/Buttons/Buttons';
import DownloadReports from './DownloadReports';
import { getCROLists } from '../../../redux/features/CRDslice';
import BulkUpload from './BulkUpload';
import { getTheDepartments } from '../../../redux/features/userSlice';
import AddBulkExpectedSessions from './AddBulkExpectedSessions';
import AnalyticsCard from './AnalyticsCard';
import { useSnackbar } from 'notistack';
import useHandleApiResponse from '../../../Hooks/useHandleApiResponse';
import { roundAmount } from '../../../utils/roundAmount';

// Days-before-deadline to switch the banner from plain info to a warning color. Deliberately
// a fixed constant, not a Config entry - kept simple on purpose.
const DEADLINE_WARNING_THRESHOLD_DAYS = 3;

const FeeForecastRegister = () => {
  const [value, setValue] = useState(dayjs());
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRowDetails, setSelectedRowDetails] = useState(null);
  const dispatch = useDispatch();
  const handleclick = (id, subject_name) => {
    window.open(`/my-workspace/academic-calendar-edit?subEnrollId=${id}&sub_name=${subject_name}`, '_blank');
  };
  const { feeForecast, loading, crmUnderdept, summary } = useSelector((state) => state.feeForecast);
  const { meta, data } = feeForecast;
  const { allowedFilters } = meta || [];

  // Deadline banner shown above the table - null when this role has no deadline configured
  // yet (meta.submissionDeadline comes back null from the backend in that case).
  const deadlineInfo = useMemo(() => {
    if (!meta?.submissionDeadline) return null;
    const deadlineMoment = dayjs(meta.submissionDeadline);
    if (!deadlineMoment.isValid()) return null;

    const formatted = deadlineMoment.format('DD MMM YYYY, hh:mm A');

    if (meta.isDeadlinePassed) {
      return { severity: 'error', text: `Submission deadline has passed (${formatted}). New submissions may be blocked.` };
    }

    // Compare calendar dates, not raw elapsed hours - dayjs().diff(..., 'day') truncates to
    // whole 24-hour blocks, so a deadline 23 hours away but on TOMORROW's date was wrongly
    // truncating to 0 and showing "today" instead of "1 day left".
    const daysRemaining = deadlineMoment.startOf('day').diff(dayjs().startOf('day'), 'day');
    if (daysRemaining <= DEADLINE_WARNING_THRESHOLD_DAYS) {
      const remainingLabel = daysRemaining <= 0 ? 'today' : `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left`;
      return { severity: 'warning', text: `Submission deadline is approaching: ${formatted} (${remainingLabel})` };
    }

    return { severity: 'info', text: `Submission deadline: ${formatted}` };
  }, [meta?.submissionDeadline, meta?.isDeadlinePassed]);
  const [enrollId, setEnrollId] = useState(null);
  const { croList } = useSelector((state) => state.crd);
  const { allDepartments } = useSelector((state) => state.users);
  const [departmentId, setDepartmentId] = useState(0);
  const [filters, setFilters] = useState([]);
  const [crm, setCrm] = useState('');
  const [cro, setcro] = useState('');
  const [forecast, setForecast] = useState('');
  const [source, setSource] = useState('');
  const [currency, setCurrency] = useState('');
  const [threshold, setThreshold] = useState(0);
  const [courseStatus, setCourseStatus] = useState('');
  const [overrideStatus, setOverrideStatus] = useState('');
  const [finalSessions, setFinalSessions] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [recalcOpen, setRecalcOpen] = useState(false);
  const [recalcRow, setRecalcRow] = useState(null);
  const [recalcLoadingId, setRecalcLoadingId] = useState(null);

  function getMatchedFilters(validations, allowedFilters) {
    return validations?.filter((item) => allowedFilters?.includes(item));
  }

  useEffect(() => {
    const validations = ['project', 'crm', 'cro'];
    const filter = getMatchedFilters(validations, allowedFilters);
    setFilters(filter);
  }, [allowedFilters]);
  const handleChangeCro = (e) => {
    setcro(e.target.value);
  };
  const handleChange = (event) => {
    const value = event.target.value;

    setDepartmentId(value);
    setCrm('');
    setcro('');

    dispatch(getCrmsUnderDept(value));
  };

  const handleChangeCrm = (e) => {
    const value = e.target.value;

    setCrm(value);
    setcro('');

    dispatch(getCROLists(value));
  };
  const handleOpen = (data) => {
    setEnrollId(data.enrollment_id);
    setFinalSessions(data.final_session);
    setOpen(true);
  };
  const forecastType = [
    { id: 0, name: 'ALL' },
    { id: 1, name: 'MANUAL' },
    { id: 2, name: 'OPTION' },
    { id: 3, name: 'PERCENTAGE' },
  ];
  const finalSource = [
    { id: 0, name: 'ALL' },
    { id: 1, name: 'CRO' },
    { id: 2, name: 'CRM' },
    { id: 3, name: 'FINANCE' },
    { id: 4, name: 'SYSTEM' },
  ];
  const currencyType = [
    { id: 0, name: 'All' },
    { id: 1, name: 'INR' },
    {
      id: 2,
      name: 'AED',
    },
    { id: 3, name: 'AED to INR' },
  ];

  const thresholdData = [
    { id: 0, name: 'ALL' },
    { id: 1, name: 'Below Threshold' },
    { id: 2, name: 'Exceeded sessions' },
    { id: 3, name: 'Low sessions' },
    { id: 4, name: 'Excluded Repeatedly' },
    { id: 5, name: 'Potential Active Courses' },
    { id: 6, name: 'Pending Balance' },
    { id: 7, name: 'Fully Paid' },
  ];

  const courseStatusData = [
    { id: 0, name: 'ALL', value: '' },
    { id: 1, name: 'Active', value: 'active' },
    { id: 2, name: 'Temporarily Inactive', value: 'temp_inactive' },
  ];

  const overrideStatusData = [
    { id: 0, name: 'ALL', value: '' },
    { id: 1, name: 'Pending', value: 'PENDING' },
    { id: 2, name: 'Approved', value: 'APPROVED' },
    { id: 3, name: 'Rejected', value: 'REJECTED' },
    { id: 4, name: 'No Override', value: 'NONE' },
  ];

  const handleThreshold = (e) => {
    setThreshold(e.target.value);
  };

  const handleCourseStatus = (e) => {
    setCourseStatus(e.target.value);
  };

  const handleOverrideStatus = (e) => {
    setOverrideStatus(e.target.value);
  };

  const handleChangeForecast = (e) => {
    setForecast(e.target.value);
  };
  const handleSource = (e) => {
    setSource(e.target.value);
  };
  const handleCurrency = (e) => {
    setCurrency(e.target.value);
  };
  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    sort: { field: '', direction: '' },
    frozenColumns: [],
    checkedRowIds: [],
    hiddenColumns: [],
  });

  const dispatchParams = useMemo(() => {
    return {
      page: tableState?.page,
      limit: tableState?.pageSize,
      search: tableState?.search,
      date: dayjs(value).format('YYYY-MM-DD'),
      sortKey: tableState.sort.field,
      sortDir: tableState.sort.direction,
      deptId: departmentId,
      crm: crm,
      cro: cro,
      forecast_typeName: forecast,
      final_source: source,
      currency_type: currency,
      threshold: threshold,
      course_status: courseStatus,
      override_status: overrideStatus,
    };
  }, [
    tableState?.page,
    tableState?.pageSize,
    tableState?.search,
    value,
    tableState.sort.field,
    tableState.sort.direction,
    departmentId,
    crm,
    cro,
    forecast,
    source,
    currency,
    threshold,
    courseStatus,
    overrideStatus,
  ]);

  const hardRefreshParams = useMemo(() => ({ ...dispatchParams, hardRefresh: true }), [dispatchParams]);

  const handleRefresh = () => {
    dispatch(getFeeForecastData(hardRefreshParams));
  };
  const { enqueueSnackbar } = useSnackbar();
  const handleApiResponse = useHandleApiResponse();
  const profile = JSON.parse(localStorage.getItem('cred')).profile;
  const isFinanceOrAudit = profile.departmentId == 7 || profile?.departmentId == 33;
  const isCRM = useMemo(() => {
    return meta?.allowedFilters?.includes('cro') && !meta?.allowedFilters?.includes('crm');
  }, [meta]);

  // Per-row recalculate: recompute a single enrollment's fee from current sale/paid values
  // against the existing sessions. Two-step: 202 confirm_required -> re-call with isConfirm=true.
  const handleRecalculate = async (row, isConfirm = false) => {
    const courseEnrollmentId = row.course_enrollment_id;
    const month = Number(dayjs(dispatchParams.date).format('M'));
    const year = Number(dayjs(dispatchParams.date).format('YYYY'));
    setRecalcLoadingId(courseEnrollmentId);
    try {
      const res = await dispatch(recalculateForecastFee({ courseEnrollmentId, month, year, isConfirm }));
      const payload = res?.payload;
      if (payload?.status === 'confirm_required') {
        setRecalcRow({
          course_enrollment_id: courseEnrollmentId,
          currentAmount: payload?.data?.currentAmount,
          overrideReason: payload?.data?.overrideReason,
          finalSessions: payload?.data?.finalSessions,
        });
        setRecalcOpen(true);
      } else if (payload?.status === 'success') {
        const d = payload?.data || {};
        enqueueSnackbar(
          d.previousAmount !== null && d.previousAmount !== undefined
            ? `Fee recalculated: ₹${d.previousAmount} → ₹${d.newAmount}`
            : `Fee calculated: ₹${d.newAmount}`,
          { variant: 'success' }
        );
        setRecalcOpen(false);
        setRecalcRow(null);
        dispatch(getFeeForecastData(hardRefreshParams));
        dispatch(getSummary(hardRefreshParams));
      } else {
        enqueueSnackbar(payload?.message || 'Something went wrong, please retry', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar('Something went wrong, please retry', { variant: 'error' });
    } finally {
      setRecalcLoadingId(null);
    }
  };

  const [selectedRowsCache, setSelectedRowsCache] = useState({});

  useEffect(() => {
    setSelectedRowsCache((prevCache) => {
      if (!tableState.checkedRowIds || tableState.checkedRowIds.length === 0) {
        return Object.keys(prevCache).length === 0 ? prevCache : {};
      }

      let cacheUpdated = false;
      const newCache = { ...prevCache };

      // 1. Add currently visible selected rows to cache
      if (data && data.length > 0) {
        tableState.checkedRowIds.forEach((id) => {
          if (!newCache[id]) {
            const row = data.find((r) => r.course_enrollment_id === id);
            if (row) {
              newCache[id] = row;
              cacheUpdated = true;
            }
          }
        });
      }

      // 2. Remove rows from cache that are no longer checked
      const checkedSet = new Set(tableState.checkedRowIds);
      Object.keys(newCache).forEach((id) => {
        const idNum = Number(id);
        const parsedId = isNaN(idNum) ? id : idNum;
        if (!checkedSet.has(parsedId)) {
          delete newCache[id];
          cacheUpdated = true;
        }
      });

      return cacheUpdated ? newCache : prevCache;
    });
  }, [tableState.checkedRowIds, data]);

  const selectedPendingLogs = useMemo(() => {
    return Object.values(selectedRowsCache)
      .filter((row) => row && row.fee_log_id && row.approval_status === 'PENDING');
  }, [selectedRowsCache]);

  const handleBulkApproveReject = (action) => {
    const feeLogIds = selectedPendingLogs.map((log) => log.fee_log_id);
    if (feeLogIds.length === 0) return;

    setActionLoading(true);
    dispatch(approveForecastOverride({ feeLogIds, action })).then((res) => {
      handleApiResponse(res, {
        onSuccess: (responsePayload) => {
          const successMsg = responsePayload?.message || `Successfully processed override requests.`;
          enqueueSnackbar(successMsg, { variant: 'success' });
          setTableState((prev) => ({ ...prev, checkedRowIds: [] }));
          dispatch(getFeeForecastData(dispatchParams));
          dispatch(getSummary(dispatchParams));
        },
        onFailed: () => {
          enqueueSnackbar('Failed to process requests.', { variant: 'error' });
        },
      });
      setActionLoading(false);
    }).catch((err) => {
      console.error(err);
      setActionLoading(false);
    });
  };

  const handleSingleApproveReject = (feeLogId, action) => {
    setActionLoading(true);
    dispatch(approveForecastOverride({ feeLogIds: [feeLogId], action })).then((res) => {
      handleApiResponse(res, {
        onSuccess: (responsePayload) => {
          const successMsg = responsePayload?.message || `Successfully processed override request.`;
          enqueueSnackbar(successMsg, { variant: 'success' });
          setDetailsOpen(false);
          setSelectedRowDetails(null);
          dispatch(getFeeForecastData(dispatchParams));
          dispatch(getSummary(dispatchParams));
        },
        onFailed: () => {
          enqueueSnackbar('Failed to process request.', { variant: 'error' });
        },
      });
      setActionLoading(false);
    }).catch((err) => {
      console.error(err);
      setActionLoading(false);
    });
  };

  const columns = [
    ...FORECAST_REGISTER_TBL_HEAD,
    {
      field: 'amount_to_collect',
      headerName: 'Amount to Collect',
      width: 260,
      align: 'right',
      headerAlign: 'right',
      sortable: true,
      valueGetter: (params) => {
        const amount = roundAmount(params.row.amount_to_collect);
        const status = params.row.approval_status;
        const pendingAmount = roundAmount(params.row.pending_amount_to_collect);
        if (status === 'PENDING') return `${amount} (Pending: ${pendingAmount})`;
        if (status === 'APPROVED') return `${amount} (Overridden)`;
        if (status === 'REJECTED') return `${amount} (Rejected)`;
        return amount;
      },
      renderCell: (params) => {
        const amount = roundAmount(params.row.amount_to_collect);
        const status = params.row.approval_status;
        const pendingAmount = roundAmount(params.row.pending_amount_to_collect);

        let badge = null;
        if (status === 'PENDING') {
          badge = (
            <Chip
              label={`Pending: ${pendingAmount}`}
              size="small"
              color="warning"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRowDetails(params.row);
                setDetailsOpen(true);
              }}
              sx={{ ml: 1, fontWeight: 'bold', fontSize: '10px', height: '20px', cursor: 'pointer' }}
            />
          );
        } else if (status === 'APPROVED') {
          badge = (
            <Chip
              label="Overridden"
              size="small"
              color="success"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRowDetails(params.row);
                setDetailsOpen(true);
              }}
              sx={{ ml: 1, fontWeight: 'bold', fontSize: '10px', height: '20px', cursor: 'pointer' }}
            />
          );
        } else if (status === 'REJECTED') {
          badge = (
            <Chip
              label="Rejected"
              size="small"
              color="error"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRowDetails(params.row);
                setDetailsOpen(true);
              }}
              sx={{ ml: 1, fontWeight: 'bold', fontSize: '10px', height: '20px', cursor: 'pointer' }}
            />
          );
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
            <span style={{ fontWeight: status !== 'NONE' ? 'bold' : 'normal' }}>
              {amount}
            </span>
            {badge}
          </Box>
        );
      },
    },
    {
      field: 'Subjects',
      headerName: 'Subjects',
      width: 200,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => params.row.subjects?.map((s) => s.subject_name).join(' | ') || 'No subjects enrolled',
      renderCell: (params) => {
        if (params.row.subjects.length == 0) {
          return 'No subjects enrolled';
        }
        // const isCalender = params.row.subjects.every((item) => item.is_calendar_created === true);
        const isCalender = false;
        const sessions = params.row.forecast?.final_sessions;
        return (
          <Box>
            {params.row.subjects.map((item) => {
              return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', m: 0.5 }}>
                  <Chip
                    icon={<Icon icon="ep:edit" width="10" height="10" />}
                    onClick={() => (isCalender ? handleclick(item.subject_enrollment_id, item.subject_name) : null)}
                    label={item.subject_name || 'Not Available'}
                    disabled={meta?.isDeadlinePassed}
                    variant={item.is_calendar_created ? 'contained' : 'outlined'}
                    color="primary"
                    sx={{ fontSize: 10, height: 20, minWidth: 160 }}
                  />
                </Box>
              );
            })}
            {params.row.course_enrollment_status === 2 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', m: 0.5 }}>
                <Tooltip title="This student is Temporarily Inactive - sessions can still be submitted, but no fee amount will be calculated for them.">
                  <Chip label="Temporarily Inactive" size="small" color="warning" sx={{ fontSize: 10, height: 20 }} />
                </Tooltip>
              </Box>
            )}
            <Box>
              {' '}
              <AddExpectedSessions
                date={value}
                isDisabled={meta?.isDeadlinePassed}
                enrollId={params.row.course_enrollment_id}
                isCalendar={isCalender}
                finalSessions={sessions}
                dispatchParams={dispatchParams}
              />
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'final_sessions',
      headerName: 'Final Sessions',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => params.row.forecast?.final_sessions,
      sortable: true,
    },
    {
      field: 'forecast_status',
      headerName: 'Forecast Status',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => params.row.forecast?.status,
      sortable: true,
    },
    {
      field: 'status',
      headerName: 'Final Source',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => params.row.forecast?.final_source,
      sortable: true,
    },
    {
      field: 'Logs',
      headerName: 'Add Logs / Override',
      width: 220,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const fTypeName = params.row.forecast_typeName; // 'MANUAL', 'PERCENTAGE', or 'OPTION'
        let allowCrmEditType = false;
        if (fTypeName === 'MANUAL') allowCrmEditType = meta?.allowCrmEditManual;
        else if (fTypeName === 'PERCENTAGE') allowCrmEditType = meta?.allowCrmEditPercentage;
        else if (fTypeName === 'OPTION') allowCrmEditType = meta?.allowCrmEditTerm;
        const showLogsBtn = isFinanceOrAudit || (isCRM && allowCrmEditType);
        if (!showLogsBtn) return null;
        const recalculating = recalcLoadingId === params.row.course_enrollment_id;
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
            <AddButton
              size={'small'}
              action={() =>
                handleOpen({
                  enrollment_id: params.row.course_enrollment_id,
                  final_session: params.row.forecast?.final_sessions,
                })
              }
            >
              {isFinanceOrAudit ? 'Add Logs' : 'Override Amount'}
            </AddButton>
            {isFinanceOrAudit && (
              <Tooltip title="Recalculate fee from current sale & paid values (keeps existing sessions)">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={recalculating}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRecalculate(params.row, false);
                    }}
                  >
                    {recalculating ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Icon icon="mdi:calculator-variant-outline" width="18" height="18" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'session_completed_percentage',
      headerName: 'Session Completed Percentage',
      width: 250,
      align: 'center',
      headerAlign: 'center',
      sortable: true,
    },
    {
      field: 'actual_fee_paid_percentage',
      headerName: 'Actual Fee Paid Percentage',
      width: 230,
      align: 'center',
      headerAlign: 'center',
      sortable: true,
    },
    {
      field: 'expected_total_income',
      headerName: 'Expected Total Income',
      width: 200,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => roundAmount(params.row.expected_total_income),
      sortable: true,
    },
    {
      field: 'balance_amount',
      headerName: 'Balance Amount',
      width: 200,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => roundAmount(params.row.balance_amount),
      sortable: true,
    },
    {
      field: 'last_paid_date',
      headerName: 'Last Paid Date',
      width: 200,
      align: 'center',
      headerAlign: 'center',
      sortable: true,
    },
    {
      field: 'temporary_inactive_timestamp',
      headerName: 'Temporary Inactive Timestamp',
      width: 250,
      align: 'center',
      headerAlign: 'center',
      sortable: true,
    },
  ];

  useEffect(() => {
    dispatch(getFeeForecastData(dispatchParams));
    dispatch(getSummary(dispatchParams));
  }, [
    tableState.search,
    dispatchParams.date,
    dispatchParams.sortKey,
    dispatchParams.sortDir,
    tableState.page,
    tableState.pageSize,
  ]);

  const handleSubmitData = () => {
    dispatch(getFeeForecastData(dispatchParams));
    dispatch(getSummary(dispatchParams));
  };
  useEffect(() => {
    dispatch(getForecastConfig());
    dispatch(getManualForecast());
    dispatch(getTheDepartments());
    dispatch(getFeeForecastData(dispatchParams));
    dispatch(getSummary(dispatchParams));
  }, [dispatch]);
  useEffect(() => {
    if (filters.length == 1) {
      // setCrm(profile.userId);
      dispatch(getCROLists(profile.userId));
    }
  }, [filters]);
  return (
    <GlobalWrapper
      title={'Fee Forecast Register'}
      actions={
        <Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              views={['year', 'month']}
              value={value}
              onChange={(newValue) => setValue(newValue)}
              format="MMM YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      }
    >
      <Box sx={{ pb: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {filters.includes('project') && (
          <Box>
            <TextField
              sx={{ minWidth: 150 }}
              select
              label="Department"
              size="small"
              value={departmentId}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            >
              {[{ dept_id: 0, dept_name: 'ALL' }, ...allDepartments].map((dept) => (
                <MenuItem key={dept.dept_id} value={dept.dept_id}>
                  {dept.dept_name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {filters.includes('crm') && (
          <Box>
            <TextField
              sx={{ minWidth: 150 }}
              select
              label="CRM"
              size="small"
              value={crm || ''}
              onChange={handleChangeCrm}
              fullWidth
              InputLabelProps={{ shrink: true }}
            >
              {[{ userID: 0, name: 'ALL' }, ...crmUnderdept].map((crm) => (
                <MenuItem key={crm.userID} value={crm.userID}>
                  {crm.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {filters.includes('cro') && (
          <Box>
            <TextField
              sx={{ minWidth: 150 }}
              select
              label="CRO"
              size="small"
              value={cro}
              onChange={handleChangeCro}
              fullWidth
              InputLabelProps={{ shrink: true }}
            >
              {[{ userID: 0, name: 'ALL' }, ...croList].map((cro) => (
                <MenuItem key={cro.userID} value={cro.userID}>
                  {cro.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
        <Box>
          <TextField
            sx={{ minWidth: 150 }}
            select
            label="Forecast type"
            size="small"
            value={forecast}
            onChange={handleChangeForecast}
            fullWidth
            InputLabelProps={{ shrink: true }}
          >
            {forecastType.map((forecast) => (
              <MenuItem key={forecast.id} value={forecast.name}>
                {forecast.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <TextField
            sx={{ minWidth: 150 }}
            select
            label="Final Source"
            size="small"
            value={source}
            onChange={handleSource}
            fullWidth
            InputLabelProps={{ shrink: true }}
          >
            {finalSource.map((source) => (
              <MenuItem key={source.id} value={source.name}>
                {source.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <TextField
            sx={{ minWidth: 150 }}
            select
            label="Currency Type"
            size="small"
            value={currency}
            onChange={handleCurrency}
            fullWidth
            InputLabelProps={{ shrink: true }}
          >
            {currencyType.map((currency) => (
              <MenuItem key={currency.id} value={currency.id}>
                {currency.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <TextField
            sx={{ minWidth: 150 }}
            select
            label="Special filters"
            size="small"
            value={threshold}
            onChange={handleThreshold}
            fullWidth
            InputLabelProps={{ shrink: true }}
          >
            {thresholdData.map((threshold) => (
              <MenuItem key={threshold.id} value={threshold.id}>
                {threshold.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <TextField
            sx={{ minWidth: 150 }}
            select
            label="Course Status"
            size="small"
            value={courseStatus}
            onChange={handleCourseStatus}
            fullWidth
            InputLabelProps={{ shrink: true }}
          >
            {courseStatusData.map((status) => (
              <MenuItem key={status.id} value={status.value}>
                {status.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <TextField
            sx={{ minWidth: 150 }}
            select
            label="Override Status"
            size="small"
            value={overrideStatus}
            onChange={handleOverrideStatus}
            fullWidth
            InputLabelProps={{ shrink: true }}
          >
            {overrideStatusData.map((status) => (
              <MenuItem key={status.id} value={status.value}>
                {status.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={{ pb: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {summary.map((item) => {
            return <AnalyticsCard name={item.name} value={item.current} percentage={item.percentageChange} />;
          })}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Button variant="contained" onClick={handleSubmitData}>
            Filter
          </Button>
        </Box>
      </Box>
      {profile?.departmentId == 7 || profile?.departmentId == 33 ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', m: 1, gap: 2 }}>
          <ManualTriggers date={value} dispatchParams={dispatchParams} />
          <Config />
          <BulkUpload dispatchParams={dispatchParams} />
          <DownloadReports date={value} />
        </Box>
      ) : null}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', m: 1, gap: 2 }}>
        <AddBulkExpectedSessions dispatchParams={dispatchParams} date={value} />
      </Box>

      {isFinanceOrAudit && selectedPendingLogs.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(45deg, #1e3a8a 30%, #3b82f6 90%)',
            color: 'white',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon icon="mdi:alert-decagram" width="24" height="24" />
            <Typography variant="subtitle1" fontWeight="bold">
              {selectedPendingLogs.length} Override Request(s) Selected for Approval
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="success"
              disabled={actionLoading}
              onClick={() => handleBulkApproveReject('APPROVE')}
              sx={{ fontWeight: 'bold', backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' } }}
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Approve Selected ({selectedPendingLogs.length})
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={actionLoading}
              onClick={() => handleBulkApproveReject('REJECT')}
              sx={{ fontWeight: 'bold', backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#c62828' } }}
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Reject Selected ({selectedPendingLogs.length})
            </Button>
          </Box>
        </Paper>
      )}

      {deadlineInfo && (
        <Alert severity={deadlineInfo.severity} sx={{ mb: 2 }} icon={<Icon icon="mdi:calendar-clock" width="20" height="20" />}>
          {deadlineInfo.text}
        </Alert>
      )}

      <GlobalTable
        headers={columns}
        loading={loading}
        meta={meta}
        rows={data}
        exportParams={{ ...dispatchParams, page: 1, limit: 'no' }}
        exportApi={getFeeForecastData}
        tableState={tableState}
        setTableState={setTableState}
        handleRefresh={handleRefresh}
        rowUniqueId="course_enrollment_id"
        checkboxNeeded={isFinanceOrAudit}
      />
      <AddForecastLogs
        enrollId={enrollId}
        finalSessions={finalSessions}
        setEnrollId={setEnrollId}
        open={open}
        setOpen={setOpen}
        dispatchParams={dispatchParams}
      />
      <GlobalDialog
        title="Recalculate collection amount?"
        open={recalcOpen}
        setOpen={setRecalcOpen}
        maxWidth="xs"
        closeButton={true}
        fullWidth
        content={
          <Box>
            <Typography sx={{ mb: 1 }}>This amount was entered manually.</Typography>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              <Typography variant="body2">
                <b>Current (manual):</b> ₹{recalcRow?.currentAmount ?? '-'}
              </Typography>
              {recalcRow?.overrideReason ? (
                <Typography variant="body2">
                  <b>Reason:</b> {recalcRow.overrideReason}
                </Typography>
              ) : null}
            </Stack>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Recalculating will replace it with the system-computed value .
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setRecalcOpen(false);
                  setRecalcRow(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={recalcLoadingId === recalcRow?.course_enrollment_id}
                startIcon={
                  recalcLoadingId === recalcRow?.course_enrollment_id ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
                onClick={() => handleRecalculate({ course_enrollment_id: recalcRow?.course_enrollment_id }, true)}
              >
                Yes, recalculate
              </Button>
            </Box>
          </Box>
        }
      />
      <GlobalDialog
        title="Forecast Override Details"
        open={detailsOpen}
        setOpen={setDetailsOpen}
        maxWidth="sm"
        fullWidth
        closeButton={true}
        content={
          selectedRowDetails ? (
            <Box sx={{ p: 1 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                {selectedRowDetails.student_name} ({selectedRowDetails.admn_no})
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Course: <strong>{selectedRowDetails.course_name}</strong> | Month: <strong>{dayjs(value).format('MMMM YYYY')}</strong>
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fcfcfc', mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Collection Values
                </Typography>
                <Box>
                  <Typography variant="body2">
                    Current Forecast Amount: <strong>{selectedRowDetails.amount_to_collect}</strong>
                  </Typography>
                  {selectedRowDetails.approval_status === 'PENDING' && (
                    <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                      Requested New Amount (Decrease): <strong>{selectedRowDetails.pending_amount_to_collect}</strong>
                    </Typography>
                  )}
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fcfcfc' }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Approval Log
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Status:</Typography>
                    <Chip
                      label={selectedRowDetails.approval_status}
                      color={
                        selectedRowDetails.approval_status === 'PENDING' ? 'warning' :
                        selectedRowDetails.approval_status === 'APPROVED' ? 'success' :
                        selectedRowDetails.approval_status === 'REJECTED' ? 'error' : 'default'
                      }
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                  <Divider />
                  <Typography variant="body2">
                    Override Reason: <span style={{ color: '#555', fontStyle: 'italic' }}>
                      "{selectedRowDetails.approval_status === 'PENDING' ? selectedRowDetails.pending_override_reason : selectedRowDetails.override_reason || 'N/A'}"
                    </span>
                  </Typography>
                  <Divider />
                  <Typography variant="body2">
                    Request Date: <strong>{selectedRowDetails.updated_at ? new Date(selectedRowDetails.updated_at).toLocaleString() : '-'}</strong>
                  </Typography>
                  {selectedRowDetails.approval_status !== 'PENDING' && selectedRowDetails.approved_by_name && (
                    <>
                      <Divider />
                      <Typography variant="body2">
                        Action Taken By: <strong>{selectedRowDetails.approved_by_name}</strong>
                      </Typography>
                      <Divider />
                      <Typography variant="body2">
                        Action Date: <strong>{selectedRowDetails.approved_at ? new Date(selectedRowDetails.approved_at).toLocaleString() : '-'}</strong>
                      </Typography>
                    </>
                  )}
                </Stack>
              </Paper>
            </Box>
          ) : null
        }
        actionButton={
          selectedRowDetails && isFinanceOrAudit && selectedRowDetails.approval_status === 'PENDING' ? (
            <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'flex-end', px: 1 }}>
              <Button
                variant="outlined"
                color="inherit"
                disabled={actionLoading}
                onClick={() => setDetailsOpen(false)}
              >
                Close
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={actionLoading}
                onClick={() => {
                  handleSingleApproveReject(selectedRowDetails.fee_log_id, 'REJECT');
                }}
                startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                Reject Override
              </Button>
              <Button
                variant="contained"
                color="success"
                disabled={actionLoading}
                onClick={() => {
                  handleSingleApproveReject(selectedRowDetails.fee_log_id, 'APPROVE');
                }}
                startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                Approve Override
              </Button>
            </Box>
          ) : (
            <Button variant="outlined" onClick={() => setDetailsOpen(false)}>
              Close
            </Button>
          )
        }
      />
    </GlobalWrapper>
  );
};

export default FeeForecastRegister;
